import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import OTPVerificationModal from './OTPVerificationModal';

const UserInfoModal = ({ isOpen, onClose }) => {
  const { user, userProfile, updateProfile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    website_url: '',
    location: '',
    default_language: ''
  });
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpChannel, setOtpChannel] = useState('email');
  const [otpDestination, setOtpDestination] = useState('');
  const [justVerified, setJustVerified] = useState('');

  const languageOptions = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: 'Chinese (Mandarin)' },
    { code: 'es', name: 'Spanish' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ar', name: 'Arabic' },
    { code: 'fr', name: 'French' },
    { code: 'bn', name: 'Bengali' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ur', name: 'Urdu' },
    { code: 'id', name: 'Indonesian' },
    { code: 'de', name: 'German' },
    { code: 'ja', name: 'Japanese' },
    { code: 'tr', name: 'Turkish' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'ko', name: 'Korean' },
    { code: 'it', name: 'Italian' },
    { code: 'fa', name: 'Persian (Farsi)' }
  ];

  useEffect(() => {
    if (userProfile) {
      const first = userProfile?.first_name || ''
      const last = userProfile?.last_name || ''
      setFormData({
        first_name: first,
        last_name: last,
        phone: userProfile?.phone_number || userProfile?.phone || '',
        bio: userProfile?.bio || '',
        website_url: userProfile?.metadata?.website_url || userProfile?.website_url || '',
        location: userProfile?.metadata?.location || userProfile?.location || '',
        default_language: userProfile?.metadata?.default_language || ''
      });
    }
  }, [userProfile, isOpen]);

  useEffect(() => {
    if (isOpen) {
      refreshProfile?.();
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSave = async () => {

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data, error } = await updateProfile(formData);
      if (error) {
        setError(error?.message);
      } else {
        await refreshProfile?.();
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (userProfile) {
      setFormData({
        first_name: userProfile?.first_name || '',
        last_name: userProfile?.last_name || '',
        phone: userProfile?.phone_number || userProfile?.phone || '',
        bio: userProfile?.bio || '',
        website_url: userProfile?.metadata?.website_url || userProfile?.website_url || '',
        location: userProfile?.metadata?.location || userProfile?.location || '',
        default_language: userProfile?.metadata?.default_language || ''
      });
    }
    setIsEditing(false);
    setError('');
  };

  const handleClose = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    onClose();
  };

  const openOTP = (channel, destination) => {
    setOtpChannel(channel);
    setOtpDestination(destination || '');
    setOtpOpen(true);
  };

  const isTrue = (v) => v === true || v === 'true' || v === 1;
  const getEmailVerified = () => {
    const src = userProfile || user || {};
    return (
      isTrue(src.email_verified) ||
      isTrue(src.emailVerified) ||
      isTrue(src.is_email_verified) ||
      isTrue(src.isEmailVerified) ||
      isTrue(src.is_verified) ||
      isTrue(src.isVerified)
    );
  };
  const getPhoneVerified = () => {
    const src = userProfile || {};
    return (
      isTrue(src.phone_verified) ||
      isTrue(src.phoneVerified) ||
      isTrue(src.is_phone_verified) ||
      isTrue(src.isPhoneVerified) ||
      isTrue(src.is_verified) ||
      isTrue(src.isVerified)
    );
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      case 'moderator':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800';
      case 'user':
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">User Information</h2>
                <p className="text-sm text-muted-foreground">Manage your account details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  iconName="Edit3"
                  iconPosition="left"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex space-x-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                    iconName="Save"
                    iconPosition="left"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    iconName="X"
                    iconPosition="left"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 text-destructive bg-destructive/10 p-3 rounded-md">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 flex items-center space-x-2 text-green-600 bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* User Information Form */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="Mail" size={16} className="inline mr-2" />
                    Email Address
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className={`px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground flex-1 ${otpOpen && otpChannel === 'email' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {user?.email || userProfile?.email || 'Not provided'}
                    </div>
                    {getEmailVerified() ? (
                      <div
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border border-green-300 text-green-700 bg-green-50 dark:bg-green-900/20 ${
                          justVerified === 'email' ? 'animate-bounce' : ''
                        }`}
                        title="Verified"
                      >
                        <Icon name="CheckCircle2" size={16} className="mr-1" />
                        Verified
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openOTP('email', user?.email || userProfile?.email || '')}
                        disabled={otpOpen && otpChannel === 'email'}
                      >
                        Verify Email
                      </Button>
                    )}
                  </div>
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="User" size={16} className="inline mr-2" />
                    First Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="first_name"
                      value={formData?.first_name}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Enter your first name"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                      {userProfile?.first_name || 'Not provided'}
                    </div>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="User" size={16} className="inline mr-2" />
                    Last Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="last_name"
                      value={formData?.last_name}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Enter your last name"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                      {userProfile?.last_name || 'Not provided'}
                    </div>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="Phone" size={16} className="inline mr-2" />
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground flex-1">
                        {userProfile?.phone_number || userProfile?.phone || 'Not provided'}
                      </div>
                      {getPhoneVerified() ? (
                        <div
                          className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border border-green-300 text-green-700 bg-green-50 dark:bg-green-900/20 ${
                            justVerified === 'phone' ? 'animate-bounce' : ''
                          }`}
                          title="Verified"
                        >
                          <Icon name="CheckCircle2" size={16} className="mr-1" />
                          Verified
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openOTP('phone', userProfile?.phone_number || userProfile?.phone || '')}
                        >
                          Verify Phone
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="MapPin" size={16} className="inline mr-2" />
                    Location
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="location"
                      value={formData?.location}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="City, Country"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                      {userProfile?.metadata?.location || userProfile?.location || 'Not provided'}
                    </div>
                  )}
                </div>

                {/* Default Language */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="Globe" size={16} className="inline mr-2" />
                    Default Language
                  </label>
                  {isEditing ? (
                    <select
                      name="default_language"
                      value={formData?.default_language}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    >
                      <option value="">Select a language</option>
                      {languageOptions.map((opt) => (
                        <option key={opt.code} value={opt.code}>{`${opt.name} (${opt.code})`}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                      {(() => {
                        const code = userProfile?.metadata?.default_language || ''
                        const match = languageOptions.find(l => l.code === code)
                        return match ? `${match.name} (${match.code})` : 'Not provided'
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Website */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Icon name="Globe" size={16} className="inline mr-2" />
                  Website
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    name="website_url"
                    value={formData?.website_url}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="https://example.com"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                    {(userProfile?.metadata?.website_url || userProfile?.website_url) ? (
                      <a href={userProfile?.metadata?.website_url || userProfile?.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {userProfile?.metadata?.website_url || userProfile?.website_url}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                )}
              </div>

              {/* Bio */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">Bio</label>
                {isEditing ? (
                  <textarea
                    name="bio"
                    value={formData?.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="block w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Tell us about yourself..."
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground min-h-[68px]">
                    {userProfile?.bio || 'No bio provided'}
                  </div>
                )}
              </div>
            </div>

            {/* Account Details */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Account Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="Shield" size={16} className="inline mr-2" />
                    Account Role
                  </label>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(userProfile?.role)}`}>
                      {(userProfile?.role || 'user').charAt(0).toUpperCase() + (userProfile?.role || 'user').slice(1)}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Account Status</label>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeColor(userProfile?.status)}`}>
                      {(userProfile?.status || 'active').charAt(0).toUpperCase() + (userProfile?.status || 'active').slice(1)}
                    </span>
                  </div>
                </div>

                {/* Member Since */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Icon name="Calendar" size={16} className="inline mr-2" />
                    Member Since
                  </label>
                  <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
                    {userProfile?.created_at ? new Date(userProfile.created_at)?.toLocaleDateString() : 'Not available'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <OTPVerificationModal
        isOpen={otpOpen}
        onClose={() => setOtpOpen(false)}
        initialChannel={otpChannel}
        initialDestination={otpDestination}
        onVerified={async () => {
          await refreshProfile?.();
          setJustVerified(otpChannel);
          setTimeout(() => setJustVerified(''), 1500);
        }}
      />
    </div>
  );
};

export default UserInfoModal;
